import { format, isToday } from "date-fns";
import { ConnectProto } from "@racoon/api";
import { Namespace, type TFn } from "@racoon/i18n";
import { fromTimestamp, getEnumName } from "@racoon/lib";
import { isJobFinished } from "./job-report-state";

type TimestampLike = { seconds?: string | number | bigint; nanos?: number };

const COMPLETED_AT_FORMAT = "HH:mm dd.MM.yyyy";

/**
 * When a finished job actually ended. Null for a job still in progress (or
 * cancelled): its `factEndAt`, if any, is not a completion time to report.
 */
export function getJobCompletedAt(job: {
  status?: ConnectProto.JobStatus;
  factEndAt?: TimestampLike;
}): Date | null {
  return isJobFinished(job.status) ? fromTimestamp(job.factEndAt) : null;
}

/** Completion time in the table: always with the date — "19:32 21.08.2024". */
export function formatCompletedAtCell(date: Date): string {
  return format(date, COMPLETED_AT_FORMAT);
}

/**
 * Completion time in the photo viewer's header: the time alone for today, with
 * the date otherwise, so a multi-day date filter cannot mix days up.
 */
export function formatCompletedAtHeader(date: Date): string {
  return isToday(date) ? format(date, "HH:mm") : format(date, COMPLETED_AT_FORMAT);
}

/** When a photo reached the server, as stamped on the photo viewer: "21:34 19.01.12". */
export function formatPhotoUploadedAt(date: Date): string {
  return format(date, "HH:mm dd.MM.yy");
}

/** "{container type} ({count})", or null when the job names no container type. */
export function formatContainerGroup(job: {
  containerGroup?: { containerType?: { name?: string }; numberOfContainers?: number };
}): string | null {
  const name = job.containerGroup?.containerType?.name;
  if (!name) return null;
  return `${name} (${job.containerGroup?.numberOfContainers ?? 0})`;
}

/**
 * The job's number as people quote it: "Z" + the job id, the way client-pwa
 * shows it. Not `orderNumber`, which is an order key, not this number.
 */
export function formatJobNumber(job: { id?: bigint | number | string }): string | null {
  return job.id != null && job.id !== "" ? `Z${job.id}` : null;
}

/**
 * The photo viewer's subtitle: "{№} {container group} {job type} {MVO}", with
 * whatever the job lacks left out.
 */
export function formatJobSubtitle(
  job: {
    id?: bigint | number | string;
    containerGroup?: any;
    serviceProvider?: { name?: string };
  },
  jobTypeLabel: string | null,
): string {
  return [formatJobNumber(job), formatContainerGroup(job), jobTypeLabel, job.serviceProvider?.name]
    .filter((part) => part?.trim())
    .join(" ");
}

/**
 * The failure reason a driver picked for a "Невывоз". The driver app stores it
 * in `driverComment` as "<key>=<reason>:<details>", so the reason is what sits
 * between "=" and ":". Null when no reason can be read out.
 */
export function getNotCompletedReason(driverComment: string | null | undefined): string | null {
  const reason = driverComment?.split("=")[1]?.split(":")[0]?.trim();
  return reason || null;
}

/** The job type's label, or null for an unset type. */
export function getJobTypeLabel(t: TFn, type: ConnectProto.JobType | undefined): string | null {
  if (!type) return null;
  const name = getEnumName(ConnectProto.JobTypeSchema, type);
  return name ? t(Namespace.Enum, name) : null;
}
